import type { ChatbotRequestContext, ChatbotResponse } from '@ev-tech/search-modules-api';

import { createChatbotRequest, isSkillDataValid, resolveSkills, responseSchema } from '../utils/conversation';
import type { ConversationMessage, PageSkill, SkillStatus } from '../types';
import type { ChatbotStore } from '../stores/useChatbot';

export const prepareRequest = (
  store: ChatbotStore,
  { message, retry, context }: { message: string; retry: boolean; context?: ChatbotRequestContext },
) => {
  const state = store.getState();
  const userMessage: ConversationMessage | undefined = retry
    ? state.conversation.find((entry) => entry.id === state.retryMessageId)
    : { id: crypto.randomUUID(), message, messageType: 'USER' };

  if (!userMessage) return;

  const conversation = retry ? state.conversation : [...state.conversation, userMessage];

  state.setConversation(conversation, userMessage.id);
  state.setDraft('');

  const requestedSkills = resolveSkills(state.pageSkills);
  const requestContext = Object.keys(state.context).length ? { ...context, ...state.context } : context;
  const request = createChatbotRequest(conversation, { skills: requestedSkills, context: requestContext });

  return { request, requestedSkills };
};

const executeCallbackSkill = async (
  store: ChatbotStore,
  { reply, skill, signal }: { reply: ConversationMessage; skill: PageSkill | undefined; signal: AbortSignal },
) => {
  if (reply.skillStatus !== 'executing' || !skill?.execute) return;

  let skillStatus: SkillStatus = 'complete';

  try {
    await skill.execute(reply.skillData ?? {});
  } catch {
    skillStatus = 'failed';
  }

  if (signal.aborted) return;

  store.getState().setSkillStatus(reply.id, skillStatus);
};

export const confirmSkill = async (store: ChatbotStore, { messageId, signal }: { messageId: string; signal: AbortSignal }) => {
  if (signal.aborted) return;

  const state = store.getState();
  const reply = state.conversation.find((message) => message.id === messageId);

  if (reply?.skillStatus !== 'awaiting-confirmation') return;

  const skill = resolveSkills(state.pageSkills).get(reply.skillName ?? '');

  if (!skill || !isSkillDataValid(skill, reply)) {
    state.setSkillStatus(messageId, skill ? 'invalid' : 'unavailable');

    return;
  }

  const skillStatus = skill.component ? 'component' : 'executing';

  state.setSkillStatus(messageId, skillStatus);

  if (typeof skill.confirmation === 'object' && skill.confirmation.closeChat) state.setOpen(false);

  await executeCallbackSkill(store, { reply: { ...reply, skillStatus }, skill, signal });
};

export const declineSkill = (store: ChatbotStore, messageId: string) => {
  const state = store.getState();

  if (state.conversation.find((message) => message.id === messageId)?.skillStatus === 'awaiting-confirmation') {
    state.setSkillStatus(messageId, 'declined');
  }
};

export const acceptChatbotResponse = async (
  store: ChatbotStore,
  { response, requestedSkills, signal }: { response: ChatbotResponse; requestedSkills: Map<string, PageSkill>; signal: AbortSignal },
) => {
  const { success, data } = responseSchema.safeParse(response);

  if (!success) throw new Error('Invalid chatbot response');

  const { skillName } = data.chatbotConversation;
  const reply: ConversationMessage = { ...data.chatbotConversation, id: crypto.randomUUID() };
  const state = store.getState();
  const skill = skillName ? resolveSkills(state.pageSkills).get(skillName) : undefined;
  const requestedSkill = skillName ? requestedSkills.get(skillName) : undefined;

  if (skillName) {
    if (!skill || !requestedSkill) reply.skillStatus = 'unavailable';
    else if (requestedSkill.confirmation && (skill.execute !== requestedSkill.execute || skill.component !== requestedSkill.component)) {
      reply.skillStatus = 'unavailable';
    } else if (!isSkillDataValid(skill, data.chatbotConversation)) reply.skillStatus = 'invalid';
    else if (skill.confirmation) reply.skillStatus = 'awaiting-confirmation';
    else reply.skillStatus = skill.component ? 'component' : 'executing';
  }

  state.setConversation([...state.conversation, reply]);

  await executeCallbackSkill(store, { reply, skill, signal });
};
